<template>
    <div id="main">
        <div v-if="orientationWarning" class="orientation-warning"></div>

        <div class="update-warning" v-if="showReloadPopup">
            <div>{{$t("generic.update_available")}}</div>
            <button class="btn-basic bordered small btn-reload" @click.prevent="reloadContent">
                {{$t("generic.reload")}}
            </button>
        </div>

        <div class="stage-warning" v-if="isStage">
            <div>you are viewing the stage version of the app.</div>
        </div>

        <div class="header" v-if="!orientationWarning">
            <div v-if="loggedInSales && loggedInUser && loggedInUser.role != 'superadmin'" class="header--loggedin-sales header--intro">
                <LocalNotifications />
                <HeaderMenu @logout="logoutUser"/>
                <div>{{loggedInUser.shop.name}}: {{loggedInSales.firstname}} {{loggedInSales.lastname}}</div>
            </div>

            <div v-if="loggedInUser && loggedInUser.role == 'superadmin'">
                <button class="header--logout-arrow" @click.prevent="logoutUser"></button>
            </div>

            <div class="header--clock header--intro">
                <Clock />
            </div>

            <div class="header--right header--intro">
                <VersionControl v-if="loggedInUser != null && loggedInUser.shop != null && loggedInUser.shop_id == 1" />
                <router-link :to="{name: 'appointment-list'}"><img src="/img/logo.svg" /></router-link>
            </div>

            <div class="border"></div>
        </div>

        <div class="content" v-if="!orientationWarning">
            <router-view />

            <ConfirmPopup v-if="logoutUserPopupVisible"
                color="green"
                :title="$t('login.logout_user_popup')"
                @cancel="cancelUserLogout" @confirm="confirmUserLogout" />
        </div>

        <div class="footer" v-if="!orientationWarning">
            <div class="footer--cocons" v-if="loggedInSales">
                <CoconStatus v-if="!demoBedStatus"/>
                <DemoBedStatus v-else/>
            </div>

            <div class="footer--locale">
                <LocaleSelect />
            </div>
        </div>

        <div class="version">{{version}}</div>
    </div>
</template>

<script>
import CoconStatus from "./components/app/CoconStatus.vue";
import DemoBedStatus from "./components/app/DemoBedStatus.vue";
import Clock from "./components/app/Clock.vue";
import LocaleSelect from "./components/app/LocaleSelect.vue";
import LocalNotifications from "./components/app/LocalNotifications.vue";
import HeaderMenu from "./components/app/HeaderMenu.vue";
import VersionControl from "./components/app/VersionControl.vue";
import ConfirmPopup from "@/components/app/ConfirmPopup.vue";
import LoginMixin from "@/mixins/LoginMixin";
import axios from "axios";
import {measurementStore} from "./stores/measurement";
import {apiStore} from "./stores/api";
import {socketStore} from "./stores/socket";
import {crossbarStore} from "./stores/crossbar";

export default {
    name: "App",

    mixins : [LoginMixin],

    components: {
        CoconStatus,
        DemoBedStatus,
        Clock,
        LocaleSelect,
        LocalNotifications,
        ConfirmPopup,
        HeaderMenu,
        VersionControl
    },

    data() {
        return {
            logoutUserPopupVisible: false,
            orientationWarning : false,
            showReloadPopup : false,
            isStage : false,
            lastClick : (new Date()) / 1000,
            version : "",
            demoBedStatus : false
        };
    },

    created() {
        window.addEventListener("resize", () => this.onResize());
        document.addEventListener("visibilitychange", this.onVisibilityChange);
        document.addEventListener("click", this.clickDocument);

        this.onResize();
        this.checkVersion();
        this.updateDemoBedStatus();

        this.isStage = window.location.host.includes("stage");
    },

    watch: {
        async isUserLoggedIn() {
            if (this.isUserLoggedIn && this.loggedInUser.role !== "superadmin") {
                const response = await apiStore().getCurrentUser();
                const date = Date.parse(response.updated_at);
                const days = 180; // +- 6 months
                if (date < Date.now() - days * 24 * 60 * 60 * 1000) {
                    if (this.$route.name !== "account-settings") {
                        this.$router.push({name : "account-settings", params : {showPasswordWarning : true}});
                    }
                }
            }
        },

        // watch router change
        $route() {
            this.updateDemoBedStatus();
        }
    },

    methods : {
        logoutUser() {
            this.logoutUserPopupVisible = true;
        },

        cancelUserLogout() {
            this.logoutUserPopupVisible = false;
        },

        confirmUserLogout() {
            this.logoutUserPopupVisible = false;

            apiStore().logoutUser();
            socketStore().close();
            crossbarStore().disconnect();

            this.$router.replace("/login");
        },

        onResize() {
            this.orientationWarning = window.innerHeight > window.innerWidth;

            //force height in meta, fixes viewport resize on android on keyboard show
            if (document.activeElement && document.activeElement.tagName.toLowerCase() === "input") {
                return;
            }

            document
                .querySelector("meta[name=\"viewport\"]")
                .setAttribute("content", "width=" + window.innerWidth + "px, height=" + window.innerHeight + "px initial-scale=1.0, minimum-scale=1.0, maximum-scale=1.0, user-scalable=no");
        },

        reloadContent() {
            window.location.reload();
        },

        async checkVersion() {
            // eslint-disable-next-line no-undef
            this.version = __APP_VERSION__;

            try {
                const currentVersion = await axios.get("/version.txt?v=" + Math.random());
                if (currentVersion.data.trim() !== this.version.trim() && !window.location.host.includes("localhost")) {
                    setTimeout(() => {
                        this.showReloadPopup = true;
                    }, 5000);
                }
            } catch (error) {
                console.log("version fetch error", error);
            }

            console.log("%cErgosleep Cocon " + this.version, "color: white; background: #1A1E57;padding : 2px;");
        },

        onVisibilityChange() {
            if (document.visibilityState === "visible") {
                this.checkVersion();
                measurementStore().pingCoconService();
            }
        },

        clickDocument() {
            const timestamp = (new Date()) / 1000;
            if (Math.abs(timestamp - this.lastClick) > 600) {
                this.checkVersion();
            }
            this.lastClick = timestamp;
        },

        updateDemoBedStatus() {
            const currentRoute = this.$router.currentRoute;
            this.demoBedStatus = currentRoute.value.meta.demoBedStatus === true;
        }
    }
};
</script>

<style scoped lang="scss">
    #main {
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        position: absolute;
        z-index: 1;
        width : 100%;
        height : 100%;
        touch-action : none;
        background-color: white;
    }

    .offline-warning {
        background: #CE1719;
        color: white;
        position: absolute;
        display: inline-block;
        z-index: 4;
        width: 40vw;
        left: 50%;
        margin-left: -20vw;
        text-align: center;
    }

    .update-warning {
        background : $color-blue;
        color: white;
        position: absolute;
        display: inline-block;
        z-index: 4;
        width: 100vw;
        left: 0;
        text-align: center;
        padding : 1rem;
        display: flex;
        align-items: center;
        flex-direction: column;
        justify-content: center;

        .btn-reload {
            margin-top : .35rem;
        }
    }

    .stage-warning {
        background : $color-red;
        color: white;
        position: absolute;
        display: inline-block;
        z-index: 4;
        width: 100vw;
        left: 0;
        text-align: center;
        font-size : .5em;
        display: flex;
        align-items: center;
        flex-direction: column;
        justify-content: center;
    }

    .orientation-warning {
        position: fixed;
        top: 8%;
        bottom : 0;
        left : 0;
        right : 0;
        background-image : url('/img/orientation-warning.png');
        background-color : white;
        background-repeat: no-repeat;
        background-position: center;
        background-size : 50%;
        width : 100%;
        height: 92%;
        z-index: 666;
    }

    .header {
        height : 8%;
        position: absolute;
        top : 0px;
        width : 100%;
        z-index: 2;

        .border {
            background : black;
            width : 100vw;
            height : 1px;
            bottom : 0px;
            position: absolute;
            @include widthHorizontalIntro(500ms, 50ms, center);
        }

        &--intro {
            z-index: 1;
            @for $i from 1 through 3 {
                &:nth-child(#{$i}) {
                    @include slideIntroAnimation(basicSlideIntro, 750ms, #{$i * 250ms});
                }
            }
        }

        &--loggedin-sales {
            position: absolute;
            left: 2%;
            top: 10%;
            font-weight: bold;
            display: flex;
            height: 80%;
            align-items: center;
        }
        &--logout-arrow {
            @extend .reset-btn;
            background-image : url('/img/icon-logout.svg');
            background-repeat : no-repeat;
            background-size: 60%;
            background-position: center;
            width: 2rem;
            height: 1.3rem;
            position: absolute;
            top: 2em;
            left:2em;
        }

        &--clock {
            width : 25vw;
            margin : auto;
            margin-top : 10px;
        }

        &--right {
            position: absolute;
            top: 30%;
            right: 2%;
            height: 50%;
            display : flex;
            width: 25%;
            justify-content: flex-end;

            img {
                height: 100%;
            }
        }
    }

    .footer {
        height : 8%;
        width : 100%;
        position: absolute;
        bottom : 0px;
        z-index: 1;

        &--cocons {
            left : 1vw;
            position: absolute;
            margin-top: .5rem;
        }

        &--locale {
            position: absolute;
            bottom: 1.5em;
            right: 2vw;
        }
    }

    .content {
        top: 8%;
        bottom: 8%;
        width: 100%;
        height: 84%;
        position: absolute;
        z-index: 1;
        overflow: hidden;
    }

    .c-cocons {
        position: absolute;
        bottom : 0;
        left: 0;
    }

    .version {
        position: absolute;
        bottom: 0.25em;
        right: 0.5em;
        font-size: 0.6em;
    }
</style>
